/** @format */

import React, { useState } from 'react'
import {
  _$,
  Icon,
  Pagination,
  Table,
  TableColumn,
  TableSortValue,
  Tooltip
} from '@king-design/react'
import styled from 'styled-components'
import { useHistory, useLocation } from 'react-router'
import { PageProps, initPage } from 'src/utils/interface'
import { MonitorSearch } from 'src/components'
import { useAsync } from 'react-use'
import { getServiceList } from 'src/api/ApiAIObservability'
import { TableSearch, parseTableSearchParams } from 'src/components'
import { getLastReportTimeDotColor } from '../../constant'
import variable from 'src/static/styles/var'

interface ServiceListProps {
  className?: string
  startTime: number
  endTime: number
  region: string
  quickKey: string
}

interface SearchProps extends PageProps {
  ServiceName: string
}

const ServiceOverview: React.FC<ServiceListProps> = ({
  className,
  startTime,
  endTime,
  region,
  quickKey
}) => {
  const history = useHistory()
  const location = useLocation()
  const [tableSort, setTableSort] = useState<TableSortValue>()

  const [search, setSearch] = useState<SearchProps>(
    () =>
      parseTableSearchParams<SearchProps>(location.search) || {
        ...initPage,
        ServiceName: ''
      }
  )

  const { value: serviceData, loading } = useAsync(async () => {
    if (startTime && endTime) {
      const result = await getServiceList({
        StartTime: startTime,
        EndTime: endTime,
        Page: search.Page,
        PageSize: search.PageSize,
        ServiceName: search.ServiceName,
        Region: region,
        Order: tableSort?.type,
        OrderBy: tableSort?.key
      })
      return {
        data: result?.Services || [],
        total: result?.TotalCount || 0
      }
    }
    return {}
  }, [startTime, endTime, search.Page, search.PageSize, search.ServiceName, tableSort, region])

  return (
    <div className={className} id="service-list-table">
      <div className="service-list-header">
        <h2 className="service-list-header-title">{_$('服务列表')}</h2>
        <TableSearch params={search}>
          <MonitorSearch
            placeholder={_$('请输入服务名称，支持模糊搜索')}
            value={search.ServiceName}
            onChange={value => {
              setSearch({
                ...search,
                Page: 1,
                ServiceName: value
              })
            }}
          />
        </TableSearch>
      </div>
      <Table
        data={serviceData?.data || []}
        rowKey={data => data.Id}
        checkType="none"
        animation={false}
        loading={loading}
        sort={tableSort}
        onChangeSort={sort => {
          setTableSort(sort)
        }}
      >
        <TableColumn
          key="ServiceName"
          title={_$('服务名称')}
          slotTemplate={([data]) => {
            return (
              <a
                onClick={() => {
                  const quick = quickKey && quickKey !== 'custom'
                  let url = `/monitor/aiobservability/servicedetail?serviceName=${data.ServiceName}`
                  if (quick) {
                    url += `&quickKey=${quickKey}`
                  } else {
                    url += `&startTime=${startTime}&endTime=${endTime}`
                  }
                  history.push(url)
                }}
              >
                {data.ServiceName}
              </a>
            )
          }}
        />
        <TableColumn
          key="LastServiceReportTime"
          sortable
          title={_$('最近数据上报时间')}
          slotTitle={
            <>
              <span title={_$('最近数据上报时间')} className="title" style={{ marginRight: 4 }}>
                {_$('最近数据上报时间')}
              </span>
              <Tooltip
                theme="light"
                hoverable
                container="#service-list-table"
                slotContent={
                  <ul className="last-report-time-tooltip-list">
                    <li className="last-report-time-container">
                      <i
                        className="last-report-time-dot"
                        style={{
                          backgroundColor: variable.successColor
                        }}
                      />
                      <span>{_$('时间差 ≤1 小时')}</span>
                    </li>
                    <li className="last-report-time-container">
                      <i
                        className="last-report-time-dot"
                        style={{
                          backgroundColor: variable.warningColor
                        }}
                      />
                      <span>{_$('时间差 ＞1 小时且 ≤ 1 天')}</span>
                    </li>
                    <li className="last-report-time-container">
                      <i
                        className="last-report-time-dot"
                        style={{
                          backgroundColor: variable.errorColor
                        }}
                      />
                      <span>{_$('时间差 ＞1 天')}</span>
                    </li>
                  </ul>
                }
              >
                <Icon className="k-icon-information" />
              </Tooltip>
            </>
          }
          slotTemplate={([data]) => {
            return (
              <div className="last-report-time-container">
                <i
                  className="last-report-time-dot"
                  style={{ backgroundColor: getLastReportTimeDotColor(data.LastServiceReportTime) }}
                />
                <span>{data.LastServiceReportTime}</span>
              </div>
            )
          }}
        />
        <TableColumn key="ServiceLanguage" title={_$('服务语言')} />
        <TableColumn key="IngestionSource" title={_$('采集来源')} />
        <TableColumn key="ServiceType" title={_$('服务类型')} />
      </Table>
      {serviceData !== undefined ? (
        <Pagination
          showGoto
          value={search.Page}
          limit={search.PageSize}
          total={serviceData?.total || 0}
          size="small"
          onChangeValue={value => {
            setSearch(state => {
              return {
                ...state,
                Page: value
              }
            })
          }}
          onChangeLimit={limit => {
            setSearch({
              ...search,
              PageSize: limit
            })
          }}
        />
      ) : null}
    </div>
  )
}

export default styled(ServiceOverview)`
  .service-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 16px 0;
    .service-list-header-title {
      font-size: 14px;
      font-weight: 600;
      line-height: 22px;
      color: rgba(0, 0, 0, 0.85);
    }
  }
  .last-report-time-tooltip-list {
    margin: 0;
    li {
      line-height: 18px;
      color: ${variable.descColor};
    }
  }
  .last-report-time-container {
    display: flex;
    align-items: center;
    .last-report-time-dot {
      width: 6px;
      height: 6px;
      margin-right: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }
  }
`
